/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { useAuth } from 'hooks/useAuth';
import { useAiPanelLinksEnabled } from 'modules/ai/hooks/useAiPanelLinksEnabled';
import {
  appendAiMessage,
  getAiUsage,
  setAiConversationId,
  setAiStreaming,
} from 'modules/ai/redux/aiActions';

type ChatStreamOptions = {
  societyId: string;
  conversationId: string | null;
  message: string;
  /** When true, backend may append sttok:// panel deep links in the answer */
  enablePanelLinks?: boolean;
  onConversationId?: (id: string) => void; // eslint-disable-line no-unused-vars
  onFirstChunk?: () => void; // eslint-disable-line no-unused-vars
  onDone?: () => void;
  persistOnUnmount?: boolean;
};

type SseEvent = { event: string; data: string };

const parseSseBlock = (block: string): SseEvent | null => {
  const lines = block.split('\n');
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return null;
  return { event, data: dataLines.join('\n') };
};

export const useAiChatStream = () => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth();
  const panelLinksEnabled = useAiPanelLinksEnabled();

  const isStreaming = useSelector(
    (state: State) => state.ai?.isStreaming ?? false
  );
  const streamingText = useSelector(
    (state: State) => state.ai?.streamingText ?? ''
  );
  const errorRef = useRef<string | null>(null);
  const error = errorRef.current;

  const abortRef = useRef<AbortController | null>(null);
  const persistOnUnmountRef = useRef(false);

  useEffect(
    () => () => {
      if (persistOnUnmountRef.current) {
        persistOnUnmountRef.current = false;
        return;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    },
    []
  );

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
  }, [dispatch]);

  const sendMessage = useCallback(
    async ({
      societyId,
      conversationId,
      message,
      enablePanelLinks = panelLinksEnabled,
      onConversationId,
      onFirstChunk,
      onDone,
      persistOnUnmount = false,
    }: ChatStreamOptions) => {
      errorRef.current = null;
      persistOnUnmountRef.current = persistOnUnmount;
      dispatch(setAiStreaming({ isStreaming: true, streamingText: '' }));

      const controller = new AbortController();
      abortRef.current = controller;
      let hasCalledFirstChunk = false;

      try {
        const token = (await getAccessTokenSilently()) as unknown as
          | string
          | null;
        const baseUrl = process.env.REACT_APP_API_URL_V2 || '';

        const response = await fetch(`${baseUrl}/ai/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({
            societyId,
            conversationId,
            message,
            enablePanelLinks: enablePanelLinks === true,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 402) {
            const body = await response.json().catch(() => ({}));
            errorRef.current = 'AI_QUOTA_EXCEEDED';
            dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
            throw Object.assign(new Error('AI quota exceeded'), {
              code: 'AI_QUOTA_EXCEEDED',
              ...body,
            });
          }
          errorRef.current = `HTTP ${response.status}`;
          dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response has no body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulated = '';
        let assistantMessageId = `assistant-${Date.now()}`;

        const dispatchFinalUsage = () => {
          dispatch(getAiUsage(societyId) as any);
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let sep = buffer.indexOf('\n\n');
          while (sep !== -1) {
            const rawBlock = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const ev = parseSseBlock(rawBlock);
            if (ev) {
              try {
                const payload = JSON.parse(ev.data);
                if (ev.event === 'start' && payload.conversationId) {
                  assistantMessageId = `assistant-${
                    payload.conversationId
                  }-${Date.now()}`;
                  dispatch(setAiConversationId(payload.conversationId));
                  onConversationId?.(payload.conversationId);
                } else if (ev.event === 'chunk' && payload.content) {
                  accumulated += payload.content;
                  dispatch(
                    setAiStreaming({
                      isStreaming: true,
                      streamingText: accumulated,
                    })
                  );
                  if (!hasCalledFirstChunk) {
                    hasCalledFirstChunk = true;
                    onFirstChunk?.();
                  }
                } else if (ev.event === 'done') {
                  dispatch(
                    appendAiMessage({
                      id: assistantMessageId,
                      role: 'assistant',
                      content: accumulated,
                      createdAt: new Date().toISOString(),
                    })
                  );
                  dispatch(
                    setAiStreaming({ isStreaming: false, streamingText: '' })
                  );
                  dispatchFinalUsage();
                  onDone?.();
                } else if (ev.event === 'error') {
                  errorRef.current = payload.message || 'Error';
                  dispatch(
                    setAiStreaming({ isStreaming: false, streamingText: '' })
                  );
                }
              } catch {
                // ignore non-JSON payloads
              }
            }
            sep = buffer.indexOf('\n\n');
          }
        }

        dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
          return;
        }
        dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
        if (!errorRef.current)
          errorRef.current = err?.message || 'Streaming failed';
        throw err;
      } finally {
        abortRef.current = null;
        persistOnUnmountRef.current = false;
      }
    },
    [dispatch, getAccessTokenSilently, panelLinksEnabled]
  );

  return {
    sendMessage,
    cancel,
    isStreaming,
    streamingText,
    error,
  };
};
